import { useEffect, useRef, useState } from "react";
import { Prompt } from "../components/Audio";
import ItemCard from "../components/ItemCard";
import Picture from "../components/Picture";
import ui from "../components/ui.module.css";
import useSentBack from "../hooks/useSentBack";

const RESET_MS = 1200;

// Never start already in order, or there is nothing to do.
const shuffled = (items) => {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  const same = copy.every((item, i) => item.value === items[i].value);
  return same ? [...copy.slice(1), copy[0]] : copy;
};

const restored = (step, saved) =>
  Array.isArray(saved?.value)
    ? saved.value.map((value) => step.items.find((item) => item.value === value)).filter(Boolean)
    : [];

// Tap a picture to put it in the next numbered box; tap a box to take it back.
// The finished order is handed in as it is, right or not; policy decides what
// happens next (Handoff 007 §3).
const SequenceStep = ({ step, saved, verdict, onDone, saving, readOnly = false }) => {
  const [tray] = useState(() => shuffled(step.items));
  const [placed, setPlaced] = useState(() => (readOnly ? restored(step, saved) : []));
  // "sent" once handed in; "wrong" while a sent-back order is shown.
  const [result, setResult] = useState(readOnly ? "sent" : null);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  useSentBack(verdict, () => {
    setResult("wrong");
    timer.current = setTimeout(() => {
      setPlaced([]);
      setResult(null);
    }, RESET_MS);
  });

  const finish = (order) => {
    setResult("sent");
    onDone({ value: order.map((item) => item.value) });
  };

  const place = (item) => {
    if (result || saving || placed.includes(item)) return;
    const next = [...placed, item];
    setPlaced(next);
    if (next.length === step.items.length) finish(next);
  };

  const takeBack = (item) => {
    if (result || saving) return;
    setPlaced((current) => current.filter((p) => p !== item));
  };

  return (
    <>
      <Prompt step={step} />

      <ol className={`${ui.slots} ${result === "wrong" ? ui.shake : ""}`}>
        {step.items.map((_, i) => {
          const item = placed[i];
          return (
            <li key={i}>
              <button
                type="button"
                className={`${ui.slot} ${item ? ui.slotFilled : ""} ${
                  verdict?.kind === "right" && item ? ui.choiceRight : ""
                }`}
                onClick={() => item && takeBack(item)}
                aria-label={item ? `${i + 1}: ${item.label}` : `${i + 1}: empty`}
              >
                <span className={ui.slotNumber} aria-hidden="true">
                  {i + 1}
                </span>
                {item && <Picture item={item} />}
              </button>
            </li>
          );
        })}
      </ol>

      <div className={ui.choices}>
        {tray.map((item) => (
          <ItemCard
            key={item.value}
            item={item}
            className={`${ui.choice} ${placed.includes(item) ? ui.placed : ""}`}
            onClick={() => place(item)}
            tabIndex={placed.includes(item) ? -1 : 0}
            aria-hidden={placed.includes(item)}
          >
            <Picture item={item} />
          </ItemCard>
        ))}
      </div>

    </>
  );
};

export default SequenceStep;
